/// <amd-module name="Repos/Views/ProjectPolicies/Components/AddProjectPoliciesPanel" />
import * as React from "react";
import * as Resources from "Repos/Views/ProjectPolicies/Resources";
import { ObservableValue } from "VSS/Core/Observable";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";
import { ConditionalChildren } from "VSSUI/ConditionalChildren";
import { TitleSize } from "VSSUI/Header";
import { Observer } from "VSSUI/Observer";
import { Panel } from "VSSUI/Panel";
import { RadioButton, RadioButtonGroup } from "VSSUI/RadioButton";
import { IProjectPoliciesService } from "../Contracts";
import { BranchPatternSelector } from "./BranchPatternSelector";

export interface IAddProjectPoliciesPanelProps {
    projectPoliciesService: IProjectPoliciesService;
    onDismiss: () => void;
}

export class AddProjectPoliciesPanel extends VssComponent<IAddProjectPoliciesPanelProps> {
    private readonly scopeTypePattern = new ObservableValue<boolean>(false);
    private readonly branchPattern = new ObservableValue<string>("");
    private readonly isValidPattern = new ObservableValue<boolean>(false);
    private defaultBranchPoliciesEnabled = false;

    constructor(props: IAddProjectPoliciesPanelProps, context: IVssComponentContext) {
        super(props, context);

        this.defaultBranchPoliciesEnabled = isFeatureFlagEnabled(context.pageContext, "SourceControl.Policy.DefaultBranchScope", false);
        if (!this.defaultBranchPoliciesEnabled) {
            this.scopeTypePattern.value = true;
        }
    }

    private static makeRefName(branchPattern: string): string {
        return (
            "refs/heads/" +
            encodeURIComponent(branchPattern)
                .replace(/%3A/gi, ":")
                .replace(/%2F/gi, "/")
                .replace(/%42/gi, "*")
        );
    }

    public render(): JSX.Element {
        const { projectPoliciesService, onDismiss } = this.props;

        const { scopeTypePattern, branchPattern, isValidPattern } = this;

        return (
            <Observer {...{ scopeTypePattern, branchPattern, isValidPattern }}>
                {() => (
                    <Panel
                        contentClassName="flex-column"
                        lightDismiss={true}
                        titleProps={{
                            text: Resources.AddProjectPolicy,
                            size: TitleSize.Large
                        }}
                        footerButtonProps={[
                            {
                                text: Resources.Cancel,
                                onClick: onDismiss
                            },
                            {
                                text: Resources.Create,
                                primary: true,
                                href: "?refName=" + (scopeTypePattern.value ? AddProjectPoliciesPanel.makeRefName(branchPattern.value) : "~default"),
                                disabled: scopeTypePattern.value && !isValidPattern.value
                            }
                        ]}
                        onDismiss={onDismiss}
                    >
                        <div className="flex-column v-scroll-auto h-scroll-hidden rhythm-vertical-8">
                            <RadioButtonGroup
                                onSelect={selectedId => {
                                    scopeTypePattern.value = selectedId == "pattern";
                                }}
                                selectedButtonId={scopeTypePattern.value ? "pattern" : "default"}
                                text={Resources.BranchesToProtect}
                            >
                                {
                                    this.defaultBranchPoliciesEnabled ? <RadioButton id="default" text={Resources.RadioDefault} /> : null
                                }
                                <RadioButton id="pattern" text={Resources.RadioPattern} />
                            </RadioButtonGroup>

                            <ConditionalChildren renderChildren={scopeTypePattern.value}>
                                <BranchPatternSelector {...{ projectPoliciesService, branchPattern, isValidPattern }} />
                            </ConditionalChildren>
                        </div>
                    </Panel>
                )}
            </Observer>
        );
    }
}
